import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import {
    ArrowLeft, Check, X, MapPin, Wine, Star, ShoppingBag, TrendingUp, Mail,
    Calendar, Edit2, Save, Package, Eye,
} from "lucide-react";
import { toast } from "sonner";

export default function AdminProducerDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});

    async function load() {
        const { data: d } = await api.get(`/admin/producers/${id}/full`);
        setData(d);
        setForm({
            producer_name: d.producer.producer_name,
            region: d.producer.region,
            story: d.producer.story,
            cover_image: d.producer.cover_image || "",
        });
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    async function toggleApproval() {
        await api.post(`/admin/producers/${id}/toggle-approval`);
        toast.success("Estado atualizado");
        load();
    }

    async function save() {
        await api.patch(`/admin/producers/${id}`, form);
        toast.success("Produtor atualizado");
        setEditing(false);
        load();
    }

    async function approveProduct(pid) {
        await api.post(`/products/${pid}/approve`);
        toast.success("Produto aprovado");
        load();
    }

    if (!data) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;
    const { producer, user, products, reviews, stats, recent_orders } = data;

    return (
        <div className="space-y-8">
            {/* Back nav */}
            <button
                onClick={() => nav("/admin/produtores")}
                className="inline-flex items-center gap-2 label-overline text-wine hover:text-gold-deep transition-colors"
                data-testid="back-producers"
            >
                <ArrowLeft className="h-4 w-4" /> Voltar a produtores
            </button>

            {/* Hero card with cover */}
            <div className="relative bg-white border border-gold-soft/30 rounded-md overflow-hidden shadow-sm">
                <div className="relative h-56 md:h-72 bg-wine-dark">
                    {producer.cover_image && (
                        <img src={producer.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-wine-dark via-wine-dark/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-cream flex items-end justify-between gap-4 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2 text-gold-soft mb-2">
                                <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />
                                <span className="label-overline">{producer.region}</span>
                            </div>
                            <h1 className="font-heading text-3xl md:text-5xl leading-tight text-shadow-soft">{producer.producer_name}</h1>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Link to={`/produtores/${producer.id}`} target="_blank" className="bg-cream/15 backdrop-blur-sm text-cream label-overline px-4 py-2 rounded-md hover:bg-cream/25 inline-flex items-center gap-2">
                                <Eye className="h-3.5 w-3.5" /> Ver público
                            </Link>
                            <button
                                onClick={toggleApproval}
                                className={`label-overline px-4 py-2 rounded-md inline-flex items-center gap-2 transition-colors ${
                                    producer.approved
                                        ? "bg-destructive/90 text-cream hover:bg-destructive"
                                        : "bg-vineyard text-cream hover:bg-vineyard/85"
                                }`}
                                data-testid="toggle-approval-producer"
                            >
                                {producer.approved ? <><X className="h-3.5 w-3.5" /> Suspender</> : <><Check className="h-3.5 w-3.5" /> Aprovar</>}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-4 gap-4 border-b border-wine-dark/5">
                    <Stat label="Estado" value={producer.approved ? "Aprovado" : "Pendente"} accent={producer.approved ? "text-vineyard" : "text-gold-deep"} />
                    <Stat label="Email" value={user?.email || "—"} small />
                    <Stat label="Membro desde" value={producer.created_at ? new Date(producer.created_at).toLocaleDateString("pt-PT") : "—"} />
                    <Stat label="ID" value={`#${producer.id.slice(0, 8)}`} mono />
                </div>
            </div>

            {/* KPI metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi icon={TrendingUp} label="Vendas totais" value={`€${stats.total_sales.toFixed(2)}`} highlight />
                <Kpi icon={ShoppingBag} label="Encomendas" value={stats.total_orders} sub={`${stats.total_units} unidades`} />
                <Kpi icon={Wine} label="Catálogo" value={stats.products_count} sub={`${stats.products_pending} pendentes`} />
                <Kpi icon={Star} label="Avaliação" value={stats.avg_rating || "—"} sub={`${stats.reviews_count} reviews`} />
            </div>

            {/* Payouts */}
            <div className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm">
                <span className="label-overline text-wine/60">Financeiro</span>
                <h2 className="font-heading text-2xl text-wine-dark mt-1 mb-4">Payouts</h2>
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <span className="label-overline text-wine-dark/60">A receber</span>
                        <p className="font-heading text-3xl text-wine-dark mt-1">€{stats.payouts_pending.toFixed(2)}</p>
                    </div>
                    <div>
                        <span className="label-overline text-wine-dark/60">Total acumulado</span>
                        <p className="font-heading text-3xl text-wine-dark mt-1">€{stats.payouts_total.toFixed(2)}</p>
                    </div>
                </div>
            </div>

            {/* Story (editable) */}
            <div className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <span className="label-overline text-wine/60">Perfil público</span>
                        <h2 className="font-heading text-2xl text-wine-dark mt-1">História & dados</h2>
                    </div>
                    {!editing ? (
                        <button onClick={() => setEditing(true)} className="btn-wine-outline text-xs px-4 py-2 inline-flex items-center gap-2" data-testid="edit-producer">
                            <Edit2 className="h-3 w-3" /> Editar
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button onClick={() => setEditing(false)} className="text-xs px-3 py-2 text-wine-dark/60 hover:text-wine-dark">Cancelar</button>
                            <button onClick={save} className="btn-wine text-xs px-4 py-2 inline-flex items-center gap-2" data-testid="save-producer">
                                <Save className="h-3 w-3" /> Guardar
                            </button>
                        </div>
                    )}
                </div>
                {!editing ? (
                    <p className="text-wine-dark/80 leading-relaxed whitespace-pre-line">{producer.story || "—"}</p>
                ) : (
                    <div className="space-y-4">
                        <EditField label="Nome" value={form.producer_name} set={(v) => setForm({ ...form, producer_name: v })} />
                        <EditField label="Região" value={form.region} set={(v) => setForm({ ...form, region: v })} />
                        <EditField label="Imagem capa (URL)" value={form.cover_image} set={(v) => setForm({ ...form, cover_image: v })} />
                        <label className="block">
                            <span className="label-overline text-wine-dark">História</span>
                            <textarea value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} rows={6} className="mt-2 w-full bg-transparent border border-wine-dark/20 rounded-md px-4 py-3 text-sm focus:border-wine" />
                        </label>
                    </div>
                )}
            </div>

            {/* Products catalog */}
            <div className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm">
                <div className="flex items-end justify-between mb-4">
                    <div>
                        <span className="label-overline text-wine/60">Catálogo</span>
                        <h2 className="font-heading text-2xl text-wine-dark mt-1">{products.length} produtos</h2>
                    </div>
                </div>
                {products.length === 0 ? (
                    <p className="text-wine-dark/60 text-sm">Sem produtos ainda.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {products.map((p) => (
                            <Link
                                to={`/admin/produtos/${p.id}`}
                                key={p.id}
                                className="border border-gold-soft/30 rounded-md overflow-hidden hover:border-wine transition-colors group"
                                data-testid={`producer-product-${p.id}`}
                            >
                                <div className="aspect-video bg-cream-warm overflow-hidden">
                                    {p.images?.[0] ? (
                                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-wine-dark/30">
                                            <Package className="h-10 w-10" strokeWidth={1} />
                                        </div>
                                    )}
                                </div>
                                <div className="p-4">
                                    <div className="flex items-start justify-between gap-2">
                                        <h4 className="font-heading text-base text-wine-dark leading-tight">{p.name}</h4>
                                        <span className={`label-overline text-[9px] px-2 py-1 rounded ${p.approved ? "text-vineyard bg-vineyard/10" : "text-gold-deep bg-gold-soft/20"}`}>
                                            {p.approved ? "Aprovado" : "Pendente"}
                                        </span>
                                    </div>
                                    <p className="text-xs text-wine-dark/60 mt-1">€{p.price.toFixed(2)} · {p.stock} stock</p>
                                    {!p.approved && (
                                        <button
                                            onClick={(e) => { e.preventDefault(); approveProduct(p.id); }}
                                            className="mt-3 w-full label-overline text-xs px-3 py-1.5 border border-vineyard/40 text-vineyard rounded hover:bg-vineyard hover:text-cream transition-colors"
                                            data-testid={`quick-approve-${p.id}`}
                                        >
                                            Aprovar
                                        </button>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            {/* Recent reviews + orders side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm">
                    <span className="label-overline text-wine/60">Reputação</span>
                    <h2 className="font-heading text-2xl text-wine-dark mt-1 mb-4">Reviews recentes</h2>
                    {reviews.length === 0 ? (
                        <p className="text-wine-dark/60 text-sm">Sem reviews.</p>
                    ) : (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                            {reviews.slice(0, 8).map((r) => (
                                <div key={r.id} className="border-b border-wine-dark/5 last:border-0 pb-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm font-semibold text-wine-dark">{r.user_name}</span>
                                        <div className="flex gap-0.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Star key={n} className={`h-3 w-3 ${n <= r.rating ? "fill-gold-soft text-gold-soft" : "text-wine-dark/20"}`} />
                                            ))}
                                        </div>
                                    </div>
                                    <p className="text-xs text-wine-dark/75 italic">"{r.comment}"</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm">
                    <span className="label-overline text-wine/60">Vendas</span>
                    <h2 className="font-heading text-2xl text-wine-dark mt-1 mb-4">Encomendas recentes</h2>
                    {recent_orders.length === 0 ? (
                        <p className="text-wine-dark/60 text-sm">Sem encomendas ainda.</p>
                    ) : (
                        <div className="space-y-2">
                            {recent_orders.map((o) => (
                                <div key={o.id} className="flex items-center justify-between py-2 border-b border-wine-dark/5 last:border-0">
                                    <div>
                                        <p className="font-mono text-xs text-wine-dark">#{o.id.slice(0, 8)}</p>
                                        <p className="text-xs text-wine-dark/60">{new Date(o.created_at).toLocaleDateString("pt-PT")} · {o.items} itens</p>
                                    </div>
                                    <span className="font-heading text-base text-wine-dark">€{o.total.toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, accent, small, mono }) {
    return (
        <div>
            <span className="label-overline text-wine-dark/60">{label}</span>
            <p className={`mt-1 ${mono ? "font-mono" : "font-heading"} ${small ? "text-sm break-all" : "text-lg"} ${accent || "text-wine-dark"}`}>{value}</p>
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub, highlight }) {
    return (
        <div className={`p-5 rounded-md border shadow-sm ${highlight ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex justify-between items-start">
                <span className={`label-overline ${highlight ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                <Icon className={`h-4 w-4 ${highlight ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />
            </div>
            <p className="font-heading text-3xl mt-2 leading-none">{value}</p>
            {sub && <span className={`text-xs mt-2 inline-block ${highlight ? "text-cream/60" : "text-wine-dark/55"}`}>{sub}</span>}
        </div>
    );
}

function EditField({ label, value, set }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark">{label}</span>
            <input value={value} onChange={(e) => set(e.target.value)} className="mt-2 w-full bg-transparent border border-wine-dark/20 rounded-md px-3 py-2 text-sm focus:border-wine" />
        </label>
    );
}
